import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { CountrySelectionService } from '../../services/country-selection.service';
import { MyKnowledgeService, KnowledgeMenuItem, HotTopicItem } from '../../services/my-knowledge.service';
import { map, switchMap, startWith, tap } from 'rxjs/operators';
import { ApiConfigService } from '../../services/api-config.service';
import { LoggingService } from '../../services/logging.service';

@Component({
  selector: 'app-my-knowledge',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './my-knowledge.component.html',
  styleUrls: ['./my-knowledge.component.css']
})
export class MyKnowledgeComponent implements OnInit {
  hotTopics$: Observable<HotTopicItem[]> = of([]);
  menuItems$: Observable<KnowledgeMenuItem[]> = of([]);
  displayItems$: Observable<any[]> = of([]);
  
  activeTab: 'hot_topics' | 'menu' = 'hot_topics';
  baseUrl: string = '';
  levelHistory: string[] = ['top'];
  selectedMenuId: string | null = null;
  selectedCountry: any;

  constructor(
    private myKnowledgeService: MyKnowledgeService,
    private router: Router,
    private apiConfigService: ApiConfigService,
    private countrySelectionService: CountrySelectionService,
    private loggingService: LoggingService
  ) {
    this.countrySelectionService.selectedCountry$.subscribe(country => {
      this.selectedCountry = country;
      this.hotTopics$ = this.myKnowledgeService.getHotTopics(undefined, country.id);
      this.menuItems$ = this.myKnowledgeService.getMenuItems();
      this.baseUrl = this.apiConfigService.getApiUrl('');

      const navigation = this.router.getCurrentNavigation();
      const state = navigation?.extras.state as { item: KnowledgeMenuItem, fromDesignerCard: boolean, thematicAreaId: string };

      if (state?.fromDesignerCard) {
        this.activeTab = 'menu';
        this.levelHistory = ['sub'];
        this.selectedMenuId = state.item.tid;
        this.displayItems$ = this.menuItems$.pipe(
          map(items => items.filter(item => item.parent_term_id === this.selectedMenuId))
        );
      } else if (state?.thematicAreaId) {
        this.activeTab = 'menu';
        this.levelHistory = ['content'];
        this.displayItems$ = this.myKnowledgeService.getContentByThematicArea(state.thematicAreaId, this.selectedCountry.id);
      } else {
        this.displayItems$ = this.menuItems$.pipe(
          map(items => items.filter(item => item.parent_term_name === ''))
        );
      }
    });
  }

  get level(): string {
    return this.levelHistory[this.levelHistory.length - 1];
  }

  decode(text: string): string {
    if (typeof text !== 'string') {
      return '';
    }
    const parser = new DOMParser();
    const decodedString = parser.parseFromString(`<!doctype html><body>${text}`, 'text/html').body.textContent;
    return decodedString || '';
  }

  ngOnInit(): void {}

  selectTab(tab: 'hot_topics' | 'menu'): void {
    this.activeTab = tab;
    this.levelHistory = ['top'];
    this.displayItems$ = this.menuItems$.pipe(
      map(items => items.filter(item => item.parent_term_name === ''))
    );
    this.loggingService.logEvent('select_content', {
      content_type: 'tab',
      item_id: tab,
    });
  }

  onMenuItemClick(item: KnowledgeMenuItem): void {
    const itemName = item.name.trim().toLowerCase();
    this.loggingService.logEvent('select_content', {
      content_type: 'menu_item',
      item_id: itemName,
    });
    if (itemName === 'true stories') {
      this.levelHistory.push('content');
      this.displayItems$ = this.myKnowledgeService.getContentByThematicArea('312', this.selectedCountry.id);
    } else if (itemName === 'coronavirus and you') {
      this.levelHistory.push('content');
      this.displayItems$ = this.myKnowledgeService.getContentByThematicArea('311', this.selectedCountry.id);
    } else {
      this.levelHistory.push('sub');
      this.selectedMenuId = item.tid;
      this.displayItems$ = this.menuItems$.pipe(
        map(items => items.filter(item => item.parent_term_id === this.selectedMenuId))
      );
    }
  }

  onSubMenuItemClick(item: KnowledgeMenuItem): void {
    this.loggingService.logEvent('select_content', {
      content_type: 'sub_menu_item',
      item_id: item.name,
    });
    this.levelHistory.push('content');
    this.displayItems$ = this.myKnowledgeService.getContentByChildTermId(item.tid, this.selectedCountry.id);
  }

  goBack(): void {
    this.levelHistory.pop();
    if (this.levelHistory.length === 0) {
      this.router.navigate(['/home']);
      return;
    }
    const currentLevel = this.level;
    if (currentLevel === 'top') {
      this.displayItems$ = this.menuItems$.pipe(
        map(items => items.filter(item => item.parent_term_name === ''))
      );
    } else if (currentLevel === 'sub') {
      this.displayItems$ = this.menuItems$.pipe(
        map(items => items.filter(item => item.parent_term_id === this.selectedMenuId))
      );
    }
  }

  goToDetails(item: any): void {
    this.loggingService.logEvent('select_content', {
      content_type: 'knowledge_content',
      item_id: item.title,
    });
    this.router.navigate(['/my-knowledge-details', item.nid]);
  }

  onHotTopicClick(item: HotTopicItem): void {
    this.loggingService.logEvent('select_content', {
      content_type: 'hot_topic',
      item_id: item.title,
    });
    this.goToDetails(item);
  }
}
